import React from "react";
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  Instagram,
  ArrowUpRight,
  Code2,
  Server,
  Database,
  Activity,
} from "lucide-react";

import ArunImage from "../Images/arun.png";

const About = () => {
  const highlights = [
    {
      icon: <Code2 size={20} />,
      title: "Clean Code",
      description: "Maintainable and readable backend systems.",
    },
    {
      icon: <Server size={20} />,
      title: "REST APIs",
      description: "Reliable APIs designed for real applications.",
    },
    {
      icon: <Database size={20} />,
      title: "Data Layer",
      description: "Efficient database design and query optimization.",
    },
    {
      icon: <Activity size={20} />,
      title: "Production Mindset",
      description: "Testing, monitoring and performance in focus.",
    },
  ];

  return (
    <section
      id="about"
      className="relative overflow-hidden bg-[#08080c] px-6 py-28 text-white sm:px-10 lg:px-20"
    >
      <div className="absolute left-0 top-20 h-72 w-72 rounded-full bg-orange-500/[0.04] blur-[100px]" />

      <div className="relative z-10 mx-auto max-w-7xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
          className="mb-16"
        >
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-orange-400">
            About Me
          </p>

          <h2 className="max-w-3xl text-4xl font-black tracking-tight sm:text-5xl lg:text-6xl">
            Engineering with <span className="text-orange-500">purpose.</span>
          </h2>

          <p className="mt-5 max-w-2xl text-lg leading-8 text-gray-500">
            I enjoy turning business requirements into clean, scalable and
            reliable software systems.
          </p>
        </motion.div>

        <div className="grid items-center gap-16 lg:grid-cols-[0.8fr_1.2fr]">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="flex justify-center"
          >
            <div className="relative">
              <div className="absolute inset-0 scale-90 rounded-[40px] bg-orange-500/10 blur-[80px]" />

              <div className="relative overflow-hidden rounded-[40px] border border-white/10 bg-white/[0.03] p-3 shadow-2xl">
                <div className="overflow-hidden rounded-[32px] bg-[#101014]">
                  <img
                    src={ArunImage}
                    alt="Arun Pungle"
                    className="h-[420px] w-[320px] object-cover object-top grayscale-[15%] transition duration-700 hover:scale-105 hover:grayscale-0 sm:h-[480px] sm:w-[370px]"
                  />
                </div>
              </div>

              <div className="absolute -bottom-5 -right-5 rounded-2xl border border-white/10 bg-[#111116]/95 px-5 py-4 shadow-2xl backdrop-blur-xl">
                <p className="text-[10px] uppercase tracking-widest text-gray-600">
                  Currently focused on
                </p>

                <p className="mt-1 text-sm font-semibold text-orange-400">
                  Backend Engineering
                </p>
              </div>
            </div>
          </motion.div>

          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-lg leading-8 text-gray-300">
              I'm{" "}
              <span className="font-semibold text-orange-400">Arun Pungle</span>
              , a backend-focused Java developer passionate about designing
              robust server-side applications and scalable APIs.
            </p>

            <p className="mt-6 text-base leading-8 text-gray-500">
              My primary stack includes Java, Spring Boot, Spring Data JPA,
              Hibernate and MySQL. I also work with microservices,
              authentication, automated testing, CI/CD and application
              monitoring.
            </p>

            <p className="mt-6 text-base leading-8 text-gray-500">
              I believe good backend engineering is more than making an API
              work. It is about designing systems that are maintainable,
              testable, observable and ready to evolve as requirements grow.
            </p>

            {/* Highlights */}
            <div className="mt-10 grid gap-3 sm:grid-cols-2">
              {highlights.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{
                    duration: 0.5,
                    delay: index * 0.08,
                  }}
                  className="group rounded-2xl border border-white/10 bg-white/[0.025] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-orange-500/20 hover:bg-orange-500/[0.035]"
                >
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl border border-orange-500/15 bg-orange-500/10 text-orange-400 transition group-hover:scale-105">
                    {item.icon}
                  </div>

                  <h3 className="font-semibold text-gray-200">{item.title}</h3>

                  <p className="mt-1 text-sm leading-6 text-gray-600">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>

            {/* Social */}
            <div className="mt-9 flex items-center gap-3">
              <a
                href="https://github.com/ArunPungle"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-gray-400 transition hover:border-orange-500/20 hover:text-orange-400"
              >
                <Github size={17} />
                GitHub
                <ArrowUpRight
                  size={14}
                  className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <a
                href="https://www.linkedin.com/in/arun-pungle"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-gray-400 transition hover:border-orange-500/20 hover:text-orange-400"
              >
                <Linkedin size={17} />
                LinkedIn
                <ArrowUpRight
                  size={14}
                  className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                />
              </a>

              <a
                href="https://www.instagram.com/thenameisap_"
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-2 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-2.5 text-sm text-gray-400 transition hover:border-orange-500/20 hover:text-orange-400"
              >
                <Instagram size={17} />
                Instagram
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
